import { UndoStack } from "./undoStack";
import type { FeatureCollection } from "geojson";
import type { SamplePointCollection, CoreCollection } from "../../domain/sample/types";
import type { MapDrawEngine } from "../../components/map/engine/useMapDrawEngine";
import type { ToolOverlay } from "../../components/map/layers/ToolOverlayLayer";

export interface JobData {
	boundary: FeatureCollection | null;
	zones: FeatureCollection | null;
	points: SamplePointCollection | null;
	cores: CoreCollection | null;
}

/**
 * Plain class that replaces the ref-avalanche in MapEditContext.
 * Created once per MapEditProvider mount. Parent syncs props into it
 * via setters each render. Arrow fields are bound at construction so
 * tool closures always read the latest values.
 */
export class JobEditSession {
	readonly undoStack = new UndoStack();

	drawEngine: MapDrawEngine | null = null;

	private _toolOverlay: ToolOverlay | null = null;
	private _selectedJobId: string | null = null;
	private _selectedPolygonId: string | null = null;
	private _jobDataAccessor: (jobId: string) => JobData = () => ({
		boundary: null,
		zones: null,
		points: null,
		cores: null,
	});
	private _applyHandler: (
		jobId: string,
		key: "boundary" | "zones" | "points" | "cores",
		fc: FeatureCollection,
	) => void = () => {};
	private _errorHandler: (msg: string) => void = () => {};

	// Setters for parent to sync props
	set toolOverlay(v: ToolOverlay) {
		this._toolOverlay = v;
	}
	set selectedJobId(v: string | null) {
		this._selectedJobId = v;
	}
	set selectedPolygonId(v: string | null) {
		this._selectedPolygonId = v;
	}
	set jobDataAccessor(v: (jobId: string) => JobData) {
		this._jobDataAccessor = v;
	}
	set applyHandler(v: (jobId: string, key: "boundary" | "zones" | "points" | "cores", fc: FeatureCollection) => void) {
		this._applyHandler = v;
	}
	set errorHandler(v: (msg: string) => void) {
		this._errorHandler = v;
	}

	// Arrow fields = bound at construction = stable forever
	getSelectedJobId = (): string | null => this._selectedJobId;
	getSelectedPolygonId = (): string | null => this._selectedPolygonId;
	getBoundaryFC = (jobId: string): FeatureCollection | null => this._jobDataAccessor(jobId).boundary;
	getZonesFC = (jobId: string): FeatureCollection | null => this._jobDataAccessor(jobId).zones;
	getPointsFC = (jobId: string): SamplePointCollection | null => this._jobDataAccessor(jobId).points;
	getCoresFC = (jobId: string): CoreCollection | null => this._jobDataAccessor(jobId).cores;
	applyBoundary = (jobId: string, fc: FeatureCollection): void => this._applyHandler(jobId, "boundary", fc);
	applyZones = (jobId: string, fc: FeatureCollection): void => this._applyHandler(jobId, "zones", fc);
	applyPoints = (jobId: string, fc: SamplePointCollection): void =>
		this._applyHandler(jobId, "points", fc as FeatureCollection);
	applyCores = (jobId: string, fc: CoreCollection): void => this._applyHandler(jobId, "cores", fc as FeatureCollection);
	showError = (msg: string): void => this._errorHandler(msg);
	getToolOverlay = (): ToolOverlay | null => this._toolOverlay;
}
